import { useEffect, useCallback, useMemo } from 'react';

/**
 * Keyboard shortcut definition
 */
export interface KeyboardShortcut {
  key: string;
  description: string;
  ctrlKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
  metaKey?: boolean;
}

/**
 * Handler map for keyboard shortcuts
 */
export type ShortcutHandlers = {
  [key: string]: () => void;
};

/**
 * Options for useKeyboardShortcuts hook
 */
export interface UseKeyboardShortcutsOptions {
  /** Whether shortcuts are enabled (default: true) */
  enabled?: boolean;
  /** Whether to prevent default browser behavior (default: true) */
  preventDefault?: boolean;
  /** Callback when shortcuts need to be disabled (e.g., input focus) */
  onDisable?: () => void;
}

/**
 * Check if the event target is an input element where shortcuts should be disabled
 */
function isInputElement(target: EventTarget | null): boolean {
  if (!target || !(target instanceof HTMLElement)) return false;
  const tagName = target.tagName.toLowerCase();
  return (
    tagName === 'input' ||
    tagName === 'textarea' ||
    target.isContentEditable ||
    target.getAttribute('role') === 'textbox'
  );
}

/**
 * Generate a unique key for a shortcut based on modifiers
 */
function getShortcutKey(
  key: string,
  ctrlKey = false,
  shiftKey = false,
  altKey = false,
  metaKey = false
): string {
  const parts: string[] = [];
  if (ctrlKey || metaKey) parts.push('ctrl');
  if (shiftKey) parts.push('shift');
  if (altKey) parts.push('alt');
  parts.push(key.toLowerCase());
  return parts.join('+');
}

/**
 * Default shortcuts for the review interface
 */
export const REVIEW_SHORTCUTS: KeyboardShortcut[] = [
  { key: 'j', description: 'Next question' },
  { key: 'k', description: 'Previous question' },
  { key: 'a', description: 'Accept current question' },
  { key: 'm', description: 'Modify current question (open editor)' },
  { key: 'r', description: 'Reject current question' },
  { key: 'e', description: 'Expand/collapse current question' },
  { key: 'Enter', ctrlKey: true, description: 'Save modification' },
  { key: 'Escape', description: 'Cancel editing / Close modal' },
  { key: '?', description: 'Show keyboard shortcuts help' },
];

/**
 * Hook for managing keyboard shortcuts
 */
export function useKeyboardShortcuts(
  handlers: ShortcutHandlers,
  options: UseKeyboardShortcutsOptions = {}
) {
  const { enabled = true, preventDefault = true } = options;

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Skip if shortcuts are disabled
      if (!enabled) return;

      // Skip if user is typing in an input field
      if (isInputElement(event.target)) {
        // Allow Escape and Ctrl+Enter even in inputs
        const key = event.key;
        if (key !== 'Escape' && !(event.ctrlKey && key === 'Enter')) {
          return;
        }
      }

      // Generate the shortcut key
      const shortcutKey = getShortcutKey(
        event.key,
        event.ctrlKey,
        event.shiftKey,
        event.altKey,
        event.metaKey
      );

      // Find and execute the handler
      const handler = handlers[shortcutKey];
      if (handler) {
        if (preventDefault) {
          event.preventDefault();
        }
        handler();
      }
    },
    [handlers, enabled, preventDefault]
  );

  useEffect(() => {
    if (!enabled) return;

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown, enabled]);
}

/**
 * Hook specifically for the review page keyboard shortcuts
 */
export interface UseReviewKeyboardShortcutsOptions {
  /** Current question index for navigation */
  currentIndex: number;
  /** Total number of questions */
  totalQuestions: number;
  /** Whether editing mode is active */
  isEditing: boolean;
  /** Whether the help modal is open */
  isHelpOpen: boolean;
  /** Current expanded question ID */
  expandedQuestionId: string | null;
  /** List of question IDs in display order */
  questionIds: string[];
}

export interface UseReviewKeyboardShortcutsActions {
  /** Navigate to a specific question by ID */
  onNavigate: (questionId: string) => void;
  /** Accept the current question */
  onAccept: (questionId: string) => void;
  /** Start editing the current question */
  onModify: (questionId: string) => void;
  /** Reject the current question */
  onReject: (questionId: string) => void;
  /** Toggle expand/collapse for the current question */
  onToggleExpand: (questionId: string) => void;
  /** Save the current edit */
  onSaveEdit: () => void;
  /** Cancel the current edit */
  onCancelEdit: () => void;
  /** Toggle the help modal */
  onToggleHelp: () => void;
}

export function useReviewKeyboardShortcuts(
  options: UseReviewKeyboardShortcutsOptions,
  actions: UseReviewKeyboardShortcutsActions
) {
  const {
    currentIndex,
    totalQuestions,
    isEditing,
    isHelpOpen,
    expandedQuestionId,
    questionIds,
  } = options;

  const {
    onNavigate,
    onAccept,
    onModify,
    onReject,
    onToggleExpand,
    onSaveEdit,
    onCancelEdit,
    onToggleHelp,
  } = actions;

  // Get the current question ID
  const currentQuestionId = useMemo(() => {
    if (expandedQuestionId) return expandedQuestionId;
    if (questionIds.length > 0 && currentIndex >= 0 && currentIndex < questionIds.length) {
      return questionIds[currentIndex];
    }
    return null;
  }, [expandedQuestionId, questionIds, currentIndex]);

  // Get current index based on expanded question
  const effectiveIndex = useMemo(() => {
    if (expandedQuestionId) {
      const idx = questionIds.indexOf(expandedQuestionId);
      return idx >= 0 ? idx : currentIndex;
    }
    return currentIndex;
  }, [expandedQuestionId, questionIds, currentIndex]);

  const handlers = useMemo<ShortcutHandlers>(() => {
    const h: ShortcutHandlers = {};

    // Navigation: j = next, k = previous
    h['j'] = () => {
      if (isHelpOpen) return;
      const nextIndex = effectiveIndex + 1;
      if (nextIndex < totalQuestions && questionIds[nextIndex]) {
        onNavigate(questionIds[nextIndex]);
      }
    };

    h['k'] = () => {
      if (isHelpOpen) return;
      const prevIndex = effectiveIndex - 1;
      if (prevIndex >= 0 && questionIds[prevIndex]) {
        onNavigate(questionIds[prevIndex]);
      }
    };

    // Actions on current question
    h['a'] = () => {
      if (isHelpOpen || isEditing || !currentQuestionId) return;
      onAccept(currentQuestionId);
    };

    h['m'] = () => {
      if (isHelpOpen || isEditing || !currentQuestionId) return;
      onModify(currentQuestionId);
    };

    h['r'] = () => {
      if (isHelpOpen || isEditing || !currentQuestionId) return;
      onReject(currentQuestionId);
    };

    h['e'] = () => {
      if (isHelpOpen) return;
      if (currentQuestionId) {
        onToggleExpand(currentQuestionId);
      }
    };

    // Edit mode shortcuts
    h['ctrl+enter'] = () => {
      if (isEditing) {
        onSaveEdit();
      }
    };

    h['escape'] = () => {
      if (isHelpOpen) {
        onToggleHelp();
      } else if (isEditing) {
        onCancelEdit();
      }
    };

    // Help modal toggle
    h['?'] = () => {
      // Shift+/ = ? on US keyboards
      onToggleHelp();
    };

    h['shift+/'] = () => {
      onToggleHelp();
    };

    return h;
  }, [
    currentQuestionId,
    effectiveIndex,
    totalQuestions,
    questionIds,
    isEditing,
    isHelpOpen,
    onNavigate,
    onAccept,
    onModify,
    onReject,
    onToggleExpand,
    onSaveEdit,
    onCancelEdit,
    onToggleHelp,
  ]);

  useKeyboardShortcuts(handlers, { enabled: true });

  return {
    currentQuestionId,
    effectiveIndex,
    shortcuts: REVIEW_SHORTCUTS,
  };
}
